// Transporteur et conducteur (E3, maquettes T05 et T06) : missions à traiter d'abord (position ancienne, véhicule
// ou conducteur à affecter, retard), mission sur téléphone (chargement, départ, étapes, arrivée, incident,
// documents) avec la position de l'appareil si elle est autorisée, sinon déclarée ; chaque étape est enregistrée
// sur l'appareil puis synchronisée, son état affiché jusqu'à l'accusé du serveur. Flotte : véhicules et conducteurs.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { TYPES_OPERATIONS_LOT3, deProduit } from '@carburanttrack/shared/logistique';
import { api, messageErreur } from '../../api/client';
import { useSession } from '../../app/session';
import { Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { useHorsLigne } from '../../horsligne/contexte';
import { LIBELLES_ETATS_OPERATION, type EtatOperation } from '../../horsligne/file';
import { AgePosition, arrivee, EtiquetteEtatOrdre, EtiquetteException, exceptionMission, libelleEtape, LiensBons, positionAppareil, quantite, resumeOrdre, usePaquet } from './communs';
import type { FicheOrdre, LigneOrdre, MissionPaquet } from './types';

interface VueMissions {
  conducteursDuCompte: string[];
  actives: LigneOrdre[];
  recentes: LigneOrdre[];
}

interface Flotte {
  vehicules: { id: string; immatriculation: string; type: string; capacite: string; compartiments: number; mission: { id: string; numero: string; etat: string } | null; activite: string }[];
  conducteurs: { id: string; nom: string; telephone: string | null; numeroPermis: string | null; mission: { id: string; numero: string; etat: string } | null }[];
  compteurs: { vehicules: number; enMission: number; disponibles: number };
}


/** Accueil du transporteur et liste des missions (T05) ; le conducteur voit d'abord les siennes. */
export function PageMissions() {
  const { etat } = useSession();
  const paquet = usePaquet();
  const enLigne = !etat?.horsLigne;
  const q = useQuery({ queryKey: ['missions'], queryFn: async () => (await api<VueMissions>('/api/v1/missions')).donnees, enabled: enLigne, retry: false });
  const flotte = useQuery({ queryKey: ['flotte-transport'], queryFn: async () => (await api<Flotte>('/api/v1/transport/flotte')).donnees, enabled: enLigne, retry: false });
  const locales = paquet.data?.contenu.missions ?? [];
  const [nbRecentes, setNbRecentes] = useState(10);
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">{dateHeure(new Date().toISOString())}</span>
          <h1>Missions</h1>
          <p className="attenue">Missions à traiter d’abord : position ancienne, véhicule ou conducteur à affecter, retard.</p>
        </div>
        <Link className="bouton" to="/flotte">
          Flotte
        </Link>
      </div>
      {flotte.data && (
        <div className="compteurs" role="list" aria-label="Flotte">
          <div role="listitem">
            <b>{flotte.data.compteurs.vehicules}</b>véhicules
          </div>
          <div role="listitem">
            <b>{flotte.data.compteurs.enMission}</b>en mission
          </div>
          <div role="listitem">
            <b>{flotte.data.compteurs.disponibles}</b>disponibles
          </div>
        </div>
      )}
      {enLigne && q.isLoading && <Squelette lignes={5} libelle="Chargement des missions" />}
      {enLigne && q.error && <MessageErreur erreur={q.error} titre="Missions indisponibles" />}
      {q.data && (
        <>
          <section className="carte-bloc" aria-labelledby="titre-missions-actives">
            <div className="carte-bloc-tete">
              <h2 id="titre-missions-actives">Missions en cours</h2>
              <span className="attenue">
                {q.data.actives.length} mission{q.data.actives.length > 1 ? 's' : ''}, {q.data.actives.filter((m) => exceptionMission(m)).length} à surveiller
              </span>
            </div>
            {q.data.actives.length === 0 ? (
              <Vide>Aucune mission en cours.</Vide>
            ) : (
              <ol className="liste-exceptions">
                {q.data.actives.map((m, i) => {
                  const e = exceptionMission(m);
                  const mienne = Boolean(m.conducteur && q.data.conducteursDuCompte.includes(m.conducteur.id));
                  return (
                    <li key={m.id}>
                      <Link className={`exception ${e?.ton ?? ''}`} to={`/missions/${m.id}`}>
                        <span className="numero" aria-hidden="true">
                          {i + 1}
                        </span>
                        <span className="texte">
                          <span className="marque-exception">
                            {e?.libelle ?? m.etatLibelle}
                            {mienne ? ', ma mission' : ''}
                          </span>
                          <b>
                            {m.numero}, {resumeOrdre(m)}
                          </b>
                          <small>
                            {m.vehicule?.immatriculation ?? 'véhicule à affecter'}, {m.conducteur?.nom ?? 'conducteur à affecter'} ; {arrivee(m)} ; position : <AgePosition ligne={m} />
                          </small>
                        </span>
                        <Icone nom="chevron" />
                      </Link>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
          {q.data.recentes.length > 0 && (
            <section className="carte-bloc" aria-labelledby="titre-missions-recentes">
              <div className="carte-bloc-tete">
                <h2 id="titre-missions-recentes">Missions récentes</h2>
                <span className="attenue">
                  {Math.min(nbRecentes, q.data.recentes.length)} affichées sur {q.data.recentes.length}
                </span>
              </div>
              <ul className="liste-lignes">
                {q.data.recentes.slice(0, nbRecentes).map((m) => (
                  <li key={m.id}>
                    <Link to={`/livraisons/${m.id}`}>
                      <span>
                        <b>
                          {m.numero} <EtiquetteEtatOrdre etat={m.etat} libelle={m.etatLibelle} />
                        </b>
                        <small>{resumeOrdre(m)}</small>
                      </span>
                      <Icone nom="chevron" />
                    </Link>
                  </li>
                ))}
              </ul>
              {q.data.recentes.length > nbRecentes && (
                <div className="actions">
                  <button type="button" className="bouton" onClick={() => setNbRecentes(nbRecentes + 20)}>
                    Afficher {Math.min(20, q.data.recentes.length - nbRecentes)} missions de plus
                  </button>
                </div>
              )}
            </section>
          )}
        </>
      )}
      {(!enLigne || q.isError) && (
        <section className="carte-bloc" aria-labelledby="titre-missions-locales">
          <div className="carte-bloc-tete">
            <h2 id="titre-missions-locales">Missions sur cet appareil</h2>
            <span className="attenue">Dernières données synchronisées {paquet.data?.enregistreLe ? dateHeure(paquet.data.enregistreLe) : 'à une date inconnue'}</span>
          </div>
          {locales.length === 0 ? (
            <Vide>Aucune mission dans les données de cet appareil.</Vide>
          ) : (
            <ul className="liste-lignes">
              {locales.map((m) => (
                <li key={m.id}>
                  <Link to={`/missions/${m.id}`}>
                    <span>
                      <b>{m.numero}</b>
                      <small>
                        {quantite(m.quantiteChargee ?? m.volumePrevu, 'L')} {deProduit(m.produitCode)}, {m.origine ?? 'origine indisponible'} vers {m.destination ?? 'destination indisponible'}
                      </small>
                    </span>
                    <Icone nom="chevron" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

/** Accueil du profil transporteur et conducteur : la liste des missions. */
export function AccueilMissions() {
  return <PageMissions />;
}

// --- Mission sur téléphone (T06) --------------------------------------------------------------------------------

interface EtapeLocale {
  identifiantClient: string;
  type: string;
  etat: EtatOperation;
  motif: string | null;
  horodatage: string;
  lieu: string | null;
}

/** Étapes de cette mission présentes dans la file de l'appareil, avec leur état de synchronisation. */
function useEtapesLocales(ordreId: string | undefined) {
  const { synchroniseur, etat } = useHorsLigne();
  const [etapes, setEtapes] = useState<EtapeLocale[]>([]);
  useEffect(() => {
    let actif = true;
    void (async () => {
      const liste: EtapeLocale[] = [];
      for (const o of await synchroniseur.operations()) {
        if (o.type !== TYPES_OPERATIONS_LOT3.etapeMission) continue;
        try {
          const c = await synchroniseur.charge<{ ordreId: string; type: string; lieu?: string | null }>(o.identifiantClient);
          if (c.ordreId === ordreId) liste.push({ identifiantClient: o.identifiantClient, type: c.type, etat: o.etat, motif: o.motif, horodatage: o.horodatageEvenement, lieu: c.lieu ?? null });
        } catch {
          // Charge illisible (clé de session perdue) : signalée par la page de synchronisation.
        }
      }
      if (actif) setEtapes(liste);
    })();
    return () => {
      actif = false;
    };
  }, [synchroniseur, ordreId, etat.compteurs, etat.derniereSynchro]);
  return etapes;
}

export function PageMission() {
  const { id } = useParams();
  const { etat } = useSession();
  const paquet = usePaquet();
  const enLigne = !etat?.horsLigne;
  const fiche = useQuery({ queryKey: ['ordre-transport', id], queryFn: async () => (await api<FicheOrdre>(`/api/v1/ordres-transport/${id}`)).donnees, enabled: enLigne && Boolean(id), retry: false });
  const locale = paquet.data?.contenu.missions?.find((m) => m.id === id) ?? null;
  if (enLigne && fiche.isLoading) return <Squelette lignes={6} libelle="Chargement de la mission" />;
  if (!fiche.data && !locale) return <MessageErreur erreur={fiche.error ?? new Error('Mission absente des données de cet appareil')} titre="Mission indisponible" />;
  return <MissionTelephone fiche={fiche.data ?? null} locale={locale} />;
}

function MissionTelephone({ fiche, locale }: { fiche: FicheOrdre | null; locale: MissionPaquet | null }) {
  const { synchroniseur, etat: reseau } = useHorsLigne();
  const id = fiche?.id ?? locale!.id;
  const numero = fiche?.numero ?? locale!.numero;
  const etatOrdre = fiche?.etat ?? locale!.etat;
  const version = fiche ? String(fiche.version) : locale!.version;
  const etapesLocales = useEtapesLocales(id);
  const [saisie, setSaisie] = useState<null | 'etape' | 'arret' | 'incident' | 'depart' | 'arrivee'>(null);
  const [lieu, setLieu] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  // Étapes dans l'ordre chronologique des événements (la fiche les rend de la plus récente à la plus ancienne).
  const serveur = [...(fiche?.etapes.map((e) => ({ type: e.type, horodatage: e.horodatageEvenement, lieu: e.lieu })) ?? locale?.etapes ?? [])].sort((x, y) => Date.parse(x.horodatage) - Date.parse(y.horodatage));
  const aDepart = serveur.some((e) => e.type === 'depart') || etapesLocales.some((e) => e.type === 'depart' && e.etat !== 'refuse');
  const aArrivee = serveur.some((e) => e.type === 'arrivee') || etapesLocales.some((e) => e.type === 'arrivee' && e.etat !== 'refuse');
  const charge = etatOrdre !== 'emis';
  const enregistrer = useMutation({
    mutationFn: async (type: string) => {
      const pos = await positionAppareil();
      return synchroniseur.enregistrer(
        TYPES_OPERATIONS_LOT3.etapeMission,
        {
          ordreId: id,
          type,
          longitude: pos?.longitude ?? null,
          latitude: pos?.latitude ?? null,
          precisionMetres: pos?.precisionMetres ?? null,
          source: pos ? 'appareil' : 'declaree',
          lieu: lieu.trim() || null,
          commentaire: commentaire.trim() || null,
        },
        { versionObjet: version, resume: `${libelleEtape(type)}, mission ${numero}` },
      );
    },
    onSuccess: (op, type) => {
      setMessage(`${libelleEtape(type)} enregistré${type === 'etape' || type === 'arrivee' ? 'e' : ''} sur cet appareil${op.etat === 'synchronise' ? ' et synchronisé' : reseau.enLigne ? ', envoi en cours' : ' : transmis dès le retour du réseau'}.${type === 'arrivee' ? ' Faites signer le bon de livraison par le destinataire.' : ''}`);
      setSaisie(null);
      setLieu('');
      setCommentaire('');
    },
  });
  const vehicule = fiche?.vehiculeDetail ? `${fiche.vehiculeDetail.immatriculation}, ${fiche.vehiculeDetail.compartiments} compartiment${fiche.vehiculeDetail.compartiments > 1 ? 's' : ''}` : (locale?.vehiculeId ? 'véhicule affecté' : 'véhicule à affecter');
  const scelles = fiche?.scelles ?? locale?.scelles ?? [];
  const lignes: { cle: string; fait: boolean; libelle: string; note: string; sync: string | null; ton: string }[] = [
    {
      cle: 'chargement',
      fait: charge,
      libelle: 'Chargement confirmé par le dépôt',
      note: charge ? `${fiche?.dateChargement ? dateHeure(fiche.dateChargement) : locale?.dateChargement ? dateHeure(locale.dateChargement) : 'heure indisponible'}, quantité servie ${quantite(fiche?.quantiteChargee ?? locale?.quantiteChargee ?? null, 'L')}` : 'En attente du dépôt',
      sync: charge ? LIBELLES_ETATS_OPERATION.synchronise.toLowerCase() : null,
      ton: charge ? 'vert' : 'gris',
    },
  ];
  for (const e of serveur) lignes.push({ cle: `s-${e.type}-${e.horodatage}`, fait: true, libelle: libelleEtape(e.type), note: `${dateHeure(e.horodatage)}${e.lieu ? `, ${e.lieu}` : ''}`, sync: LIBELLES_ETATS_OPERATION.synchronise.toLowerCase(), ton: 'vert' });
  for (const e of etapesLocales.filter((x) => x.etat !== 'synchronise'))
    lignes.push({ cle: e.identifiantClient, fait: e.etat !== 'refuse', libelle: libelleEtape(e.type), note: `${dateHeure(e.horodatage)}${e.lieu ? `, ${e.lieu}` : ''}${e.motif ? `, ${e.motif}` : ''}`, sync: LIBELLES_ETATS_OPERATION[e.etat].toLowerCase(), ton: e.etat === 'refuse' || e.etat === 'conflit' ? 'rouge' : 'ambre' });
  if (!aArrivee) lignes.push({ cle: 'arrivee', fait: false, libelle: 'Arrivée à destination', note: fiche ? arrivee(fiche) : `arrivée prévue ${dateHeure(locale!.dateArriveePrevue)}`, sync: null, ton: 'gris' });
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">Mission en cours</span>
          <h1>
            {numero} {fiche && <EtiquetteEtatOrdre etat={fiche.etat} libelle={fiche.etatLibelle} />} {fiche && <EtiquetteException exception={exceptionMission(fiche)} />}
          </h1>
          <p>
            <b>{fiche ? resumeOrdre(fiche) : `${quantite(locale!.quantiteChargee ?? locale!.volumePrevu, 'L')} ${deProduit(locale!.produitCode)}, ${locale!.origine ?? ''} vers ${locale!.destination ?? ''}`}</b>
          </p>
          <p className="attenue">
            Citerne {vehicule}
            {scelles.length ? `, scellés ${scelles.join(' et ')}` : ''}
          </p>
        </div>
      </div>
      <section className="carte-bloc" aria-labelledby="titre-etapes-mission">
        <h2 id="titre-etapes-mission">Étapes</h2>
        <ol className="liste-lignes">
          {lignes.map((l) => (
            <li key={l.cle}>
              <span>
                <b>
                  {l.fait ? '' : 'À faire : '}
                  {l.libelle}
                </b>
                <small>{l.note}</small>
              </span>
              {l.sync && <span className={`etiquette ${l.ton}`}>{l.sync}</span>}
            </li>
          ))}
        </ol>
      </section>
      {message && <Message type="succes">{message}</Message>}
      {enregistrer.error && <Message type="erreur">{messageErreur(enregistrer.error)}</Message>}
      {etatOrdre === 'annule' ? (
        <Message type="avertissement">Mission annulée par le propriétaire du produit : aucune étape ne peut plus être enregistrée.</Message>
      ) : saisie ? (
        <form
          className="carte-bloc"
          onSubmit={(e) => {
            e.preventDefault();
            enregistrer.mutate(saisie);
          }}
        >
          <h2>{saisie === 'arrivee' ? 'Confirmer l’arrivée' : saisie === 'depart' ? 'Confirmer le départ' : saisie === 'incident' ? 'Signaler un incident' : saisie === 'arret' ? 'Déclarer un arrêt' : 'Ajouter une étape'}</h2>
          <div className="champ">
            <label htmlFor="m-lieu">Lieu</label>
            <input id="m-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)} maxLength={200} required={saisie !== 'depart' && saisie !== 'arrivee'} />
            <span className="aide">La position de l’appareil est jointe si vous l’autorisez ; sinon le lieu déclaré fait foi.</span>
          </div>
          {(saisie === 'incident' || saisie === 'arret') && (
            <div className="champ">
              <label htmlFor="m-commentaire">{saisie === 'incident' ? 'Description de l’incident' : 'Motif de l’arrêt'}</label>
              <textarea id="m-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3} maxLength={1000} required minLength={3} />
            </div>
          )}
          <div className="actions">
            <button type="submit" className="bouton primaire large" disabled={enregistrer.isPending}>
              {enregistrer.isPending ? 'Enregistrement' : 'Enregistrer'}
            </button>
            <button type="button" className="bouton" onClick={() => setSaisie(null)}>
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <div className="actions">
          {charge && !aDepart && (
            <button type="button" className="bouton primaire large" onClick={() => setSaisie('depart')}>
              Confirmer le départ
            </button>
          )}
          {charge && aDepart && !aArrivee && (
            <button type="button" className="bouton primaire large" onClick={() => setSaisie('arrivee')}>
              Confirmer l’arrivée
            </button>
          )}
          {charge && !aArrivee && (
            <>
              <button type="button" className="bouton" onClick={() => setSaisie('etape')}>
                Ajouter une étape
              </button>
              <button type="button" className="bouton" onClick={() => setSaisie('arret')}>
                Déclarer un arrêt
              </button>
            </>
          )}
          <button type="button" className="bouton" onClick={() => setSaisie('incident')}>
            Signaler un incident
          </button>
        </div>
      )}
      {!charge && <p className="attenue">Le départ s’enregistre après la confirmation du chargement par le dépôt.</p>}
      {fiche && (
        <section className="carte-bloc espace-haut" aria-labelledby="titre-documents-mission">
          <h2 id="titre-documents-mission">Documents</h2>
          <LiensBons id={fiche.id} numero={fiche.numero} bonChargement={fiche.droits.bonChargement} bonLivraison={fiche.droits.bonLivraison} />
          {fiche.canalMessagerieId && (
            <p>
              <Link to={`/messages/${fiche.canalMessagerieId}`}>Canal de la livraison</Link>
            </p>
          )}
        </section>
      )}
      <p className="attenue">
        <Link to="/synchronisation">État de la synchronisation de cet appareil</Link>
      </p>
    </div>
  );
}

// --- Flotte ----------------------------------------------------------------------------------------------------

export function PageFlotte() {
  const q = useQuery({ queryKey: ['flotte-transport'], queryFn: async () => (await api<Flotte>('/api/v1/transport/flotte')).donnees });
  const [onglet, setOnglet] = useState<'vehicules' | 'conducteurs'>('vehicules');
  if (q.isLoading) return <Squelette lignes={6} />;
  if (q.error || !q.data) return <MessageErreur erreur={q.error} titre="Flotte indisponible" />;
  const f = q.data;
  const libelleActivite = (a: string) => (a === 'en_mission' ? 'En mission' : a === 'affecte' ? 'Affecté, à charger' : 'Disponible');
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Flotte</h1>
          <p className="attenue">Véhicules et conducteurs actifs de votre organisation, avec leur mission en cours.</p>
        </div>
      </div>
      <div className="compteurs" role="list" aria-label="Compteurs de la flotte">
        <div role="listitem">
          <b>{f.compteurs.vehicules}</b>véhicules
        </div>
        <div role="listitem">
          <b>{f.compteurs.enMission}</b>en mission
        </div>
        <div role="listitem">
          <b>{f.compteurs.disponibles}</b>disponibles
        </div>
        <div role="listitem">
          <b>{f.conducteurs.length}</b>conducteurs
        </div>
      </div>
      <div className="onglets" role="tablist">
        <button type="button" role="tab" aria-selected={onglet === 'vehicules'} onClick={() => setOnglet('vehicules')}>
          Véhicules ({f.vehicules.length})
        </button>
        <button type="button" role="tab" aria-selected={onglet === 'conducteurs'} onClick={() => setOnglet('conducteurs')}>
          Conducteurs ({f.conducteurs.length})
        </button>
      </div>
      {onglet === 'vehicules' ? (
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Véhicules</caption>
            <thead>
              <tr>
                <th scope="col">Immatriculation</th>
                <th scope="col" className="masquable">
                  Type
                </th>
                <th scope="col" className="nombre">
                  Capacité
                </th>
                <th scope="col" className="nombre masquable">
                  Compartiments
                </th>
                <th scope="col">Activité</th>
              </tr>
            </thead>
            <tbody>
              {f.vehicules.map((v) => (
                <tr key={v.id}>
                  <td>{v.immatriculation}</td>
                  <td className="masquable">{v.type.replace(/_/g, ' ')}</td>
                  <td className="nombre">{quantite(v.capacite, 'L')}</td>
                  <td className="nombre masquable">{v.compartiments}</td>
                  <td>
                    {v.mission ? (
                      <Link to={`/missions/${v.mission.id}`}>
                        {libelleActivite(v.activite)}, {v.mission.numero}
                      </Link>
                    ) : (
                      libelleActivite(v.activite)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : f.conducteurs.length === 0 ? (
        <Vide>Aucun conducteur lisible avec vos habilitations.</Vide>
      ) : (
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Conducteurs</caption>
            <thead>
              <tr>
                <th scope="col">Conducteur</th>
                <th scope="col" className="masquable">
                  Téléphone
                </th>
                <th scope="col" className="masquable">
                  Permis
                </th>
                <th scope="col">Mission</th>
              </tr>
            </thead>
            <tbody>
              {f.conducteurs.map((c) => (
                <tr key={c.id}>
                  <td>{c.nom}</td>
                  <td className="masquable">{c.telephone ?? <span className="attenue">non renseigné</span>}</td>
                  <td className="masquable">{c.numeroPermis ?? <span className="attenue">non renseigné</span>}</td>
                  <td>{c.mission ? <Link to={`/missions/${c.mission.id}`}>{c.mission.numero}</Link> : <span className="attenue">aucune</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

